import { useState } from 'react'

import { GammaBadge, Icon, Search } from '@joor/design-system'

import { formatNameWithCode } from 'utils/formatters'
import { fromGlobalId } from 'utils/transformations/graphql'

import {
  AccountOption,
  BadgeWrapper,
  OptionWrapper,
  SearchBar,
  SectionTitle,
  StyledTextButton,
  TitleWrapper,
} from '../../retailerAccountsDropdown.ui'
import AccountDropdownOption from '../AccountDropdownOption/AccountDropdownOption'
import {
  LiteRetailerAccountsType,
  RecentlyCreatedAccountType,
} from 'features/Navbar/NavbarLiteRetailer/NavbarLiteRetailer.queries'
import AccountsVisibilityModal from 'features/Navbar/NavbarLiteRetailer/modals/AccountsVisibilityModal/AccountsVisibilityModal'
import RecentCreatedAccountsModal from 'features/Navbar/NavbarLiteRetailer/modals/RecentCreatedAccountsModal/RecentCreatedAccountsModal'
import { useSetSelectedAccount } from 'features/Navbar/NavbarRevamp/components/AccountsDropdown/accountsDropdown.hooks'
import { RetailAccountsDropdownIds } from 'features/Navbar/NavbarRevamp/navbarRevamp.ids'
import { GrapheneAccount } from 'features/Navbar/NavbarRevamp/navbarRevamp.types'

const OtherAccountsSection = ({
  accounts,
  userAccounts,
  recentlyCreatedAccounts = [],
}: {
  accounts: LiteRetailerAccountsType[]
  userAccounts: GrapheneAccount[]
  recentlyCreatedAccounts?: RecentlyCreatedAccountType[]
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [searchText, setSearchText] = useState<string>('')
  const [isAccountVisibilityModalOpen, setIsAccountVisibilityModalOpen] =
    useState(false)
  const [
    isRecentCreatedAccountsModalOpen,
    setIsRecentCreatedAccountsModalOpen,
  ] = useState(false)
  const { setSelectedAccount } = useSetSelectedAccount()
  const canSearchFilter =
    Boolean(searchText?.length) && Boolean(accounts.length)
  const search = canSearchFilter ? searchText.toLowerCase() : ''

  return (
    <>
      <OptionWrapper data-testid={RetailAccountsDropdownIds.OtherAccounts}>
        <TitleWrapper onClick={() => setIsVisible(!isVisible)}>
          <BadgeWrapper>
            <SectionTitle>
              {`Other Accounts (${userAccounts.length})`}
            </SectionTitle>
            {recentlyCreatedAccounts.length > 0 && (
              <GammaBadge value={recentlyCreatedAccounts.length} />
            )}
          </BadgeWrapper>
          <Icon iconName={isVisible ? 'chevron-up' : 'chevron-down'} />
        </TitleWrapper>
        {isVisible && (
          <>
            <StyledTextButton
              id={RetailAccountsDropdownIds.VisbilityModal}
              data-testid={RetailAccountsDropdownIds.VisbilityModal}
              onClick={() => setIsAccountVisibilityModalOpen(true)}
            >
              Manage Account Visibility
            </StyledTextButton>
            {recentlyCreatedAccounts.length > 0 && (
              <BadgeWrapper>
                <StyledTextButton
                  id={RetailAccountsDropdownIds.RecentAccountsModal}
                  data-testid={RetailAccountsDropdownIds.RecentAccountsModal}
                  onClick={() => setIsRecentCreatedAccountsModalOpen(true)}
                >
                  Manage New Accounts
                </StyledTextButton>
                <GammaBadge value={recentlyCreatedAccounts.length} />
              </BadgeWrapper>
            )}
            <SearchBar>
              <Search
                id={RetailAccountsDropdownIds.AccountsSearchBar}
                data-testid={RetailAccountsDropdownIds.AccountsSearchBar}
                placeholder={'Search by connected brands'}
                onSearch={({ searchText }: { searchText: string }) =>
                  setSearchText(searchText)
                }
                onKeyEnter={() => setSearchText(searchText)}
                value={searchText}
                incrementalSearch
              />
            </SearchBar>
            {userAccounts
              .filter((userAccount) => {
                const connectedAccounts =
                  accounts.find(
                    (account) =>
                      fromGlobalId(account.id).id ===
                      fromGlobalId(userAccount.id).id,
                  )?.connectedAccounts || []

                return (
                  userAccount.name.toLowerCase().includes(search) ||
                  connectedAccounts?.filter((brand) =>
                    brand?.profileName?.toLowerCase().includes(search),
                  )?.length > 0
                )
              })
              .map((userAccount) => {
                const accountId = fromGlobalId(userAccount.id).id
                const account = accounts.find(
                  (account) => accountId === fromGlobalId(account.id).id,
                )
                const accountTestId = `${RetailAccountsDropdownIds.AccountsOption}__${accountId}`

                return (
                  <AccountOption
                    key={accountTestId}
                    data-testid={accountTestId}
                  >
                    <AccountDropdownOption
                      onClick={() => setSelectedAccount(userAccount?.id)}
                      accountName={formatNameWithCode(
                        userAccount?.name,
                        accountId,
                      )}
                      connectedAccounts={account?.connectedAccounts}
                      privacy={!!account?.privacy}
                      createdAt={account?.createdAt?.toString() || ''}
                      isPrimary={!!account?.isPrimary}
                      hideVisibilityIcon={!account}
                      createdBy={
                        account?.accountCreationSource?.requestedByAccount &&
                        account?.accountCreationSource?.requestedByAccount
                          .id === account?.id
                          ? 'you'
                          : account?.accountCreationSource?.requestedByAccount
                              ?.name
                      }
                    />
                  </AccountOption>
                )
              })}
          </>
        )}
      </OptionWrapper>
      {isAccountVisibilityModalOpen && (
        <AccountsVisibilityModal
          isOpen={isAccountVisibilityModalOpen}
          onClose={() => setIsAccountVisibilityModalOpen(false)}
        />
      )}
      {isRecentCreatedAccountsModalOpen && (
        <RecentCreatedAccountsModal
          isOpen={isRecentCreatedAccountsModalOpen}
          onClose={() => setIsRecentCreatedAccountsModalOpen(false)}
          accounts={accounts}
          recentlyCreatedAccounts={recentlyCreatedAccounts}
        />
      )}
    </>
  )
}

export default OtherAccountsSection
